import { Component, ComponentRef, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { CardRippedStatus, CpxHandleServiceAbstract, SessionTypeEnum, TypeConnecteurEnum } from 'src/app/services/cpx-handle/cpx-handle-service-abstract';
import { DmpServiceAbstract } from 'src/app/services/dmp/dmp-service.abstract';
import { InsRealservice } from 'src/app/services/ins/ins.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { PatientService } from 'src/app/services/patient/patient.service';
import { Adresse } from 'src/app/utils/interfaces/new/adresse.interface';
import { DmpHlFindPatientResponseSIns } from 'src/app/utils/interfaces/new/dmp-hl-find-patient-response.interface';
import { DmpHlGetDirectAuthentificationDmpStatus, DmpStatusEnum, DmpUserAuthorizationEnum } from 'src/app/utils/interfaces/new/dmp-hl-get-direct-authentification-dmp-status.interface';
import { DmpHlAccessModeEnum } from 'src/app/utils/interfaces/new/dmp-hl-set-dmp-access-mode-params.interface';
import { DmpAuthorizationActionEnum } from 'src/app/utils/interfaces/new/dmp-hl-update-user-access-authorization-identity-params.interface';
import { InfosPatient } from 'src/app/utils/interfaces/new/infos-patient.interface';
import { isAllowedExternalUrl } from 'src/app/utils/safe-url';
import { environment } from 'src/environments/environment';
import { ActionEnum, DomainEnum, LogFonctionnelCallerService, LogFonctionnelService, ResultEnum, TypeTargetEnum } from 'src/app/services/log-fonctionnel/log-fonctionnel.service';
import { ConnectionType } from '../modal/handle-cpx-modal/handle-cpx-modal.component';
import { DmpAccessRequestModalComponent, DmpAutorizationForm } from './dmp-access-request-modal/dmp-access-request-modal.component';
import { CpxSessionInterface, CpxTypeEnum } from 'src/app/utils/interfaces/new/cpx-session.interface';
import { DmpAccessRight } from './display-dmp/display-dmp.component';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { StringUtilsService } from 'src/app/services/string-utils/string-utils.service';
import { Identity } from 'src/app/utils/interfaces/new/lecture-ins.interface';
import { DmpGetMyDocumentsResult } from 'src/app/utils/interfaces/new/dmp-get-my-documents-result.interface';
import { FeatureKey, FeatureService } from 'src/app/services/feature/feature.service';

export interface DmpStep {
  step: number;
  title: string;
}

export const DmpSteps = {
  findDmp: {
    step: 0,
    title: 'Recherche DMP patient'
  } as DmpStep,
  dmpAccessRight: {
    step: 1,
    title: 'Consulter le DMP du patient'
  } as DmpStep,
  addDocuments: {
    step: 2,
    title: 'Ajouter des documents au DMP'
  } as DmpStep,
  myDocuments: {
    step: 3,
    title: 'Mes documents'
  } as DmpStep
}

interface MyDocumentTableDisplay {
  title: string;
  meta: DmpGetMyDocumentsResult;
}

type AccesDmpType = 'explicite' | 'bris-de-glace' | 'refus';

const UNAUTHORISZED_ATTRIBUT = 'L\'attribut d\'identité de ce patient ne permet pas l\'accès aux services INS pour celui-ci';
const UNAUTHORISZED_STATUS = 'Le statut de l\'identité de ce patient n\'étant pas qualifiée l\'accès au DMP n\'est pas autorisé pour celui-ci';

@Component({
  selector: 'app-dmp',
  templateUrl: './dmp.component.html',
  styleUrls: ['./dmp.component.scss']
})
export class DmpComponent implements OnInit, OnDestroy {

  public accessible = true;

  public dmpSteps = DmpSteps;
  public currentDmpStep = undefined;
  public previousStep = undefined;
  public cardSubtitle: string;
  public displayDmp: DmpHlGetDirectAuthentificationDmpStatus;
  public isBrisDeGlace = false;
  public amIMt = false;
  private patientId: string;
  public patient: InfosPatient;
  public patientAddress: Adresse;
  public connectionType: ConnectionType = ConnectionType.unknow;           // TODO Ne pas initialiser, mais setter selon le type de connexion
  public dmpAccessRight: DmpAccessRight = DmpAccessRight.unknow;
  public dmpStatus: DmpHlGetDirectAuthentificationDmpStatus;
  private _cardStatusChange: Subscription;
  private _cardTypeSub: Subscription;
  private _cpxCardInfosSub: Subscription;
  public patientIns: string;
  public patientInsIdentity: Identity;
  public autorisationAlimentation = false;

  public myDocsTable: Paginated<MyDocumentTableDisplay>;
  public myDocsTableColumnTable = [
    { title: 'Titre', data: 'title' },
    { title: 'Supprimer', data: null },
  ];
  isSecretMode: boolean;
  public isDmpSegurAccess: boolean = false;

  private _logCallSrv: LogFonctionnelCallerService;

  constructor(
    private _dmpSrv: DmpServiceAbstract,
    private _modalSrv: ModalService,
    private _viewContainerRef: ViewContainerRef,
    private _activatedRoute: ActivatedRoute,
    private _patientSrv: PatientService,
    private _cpxHandleSrv: CpxHandleServiceAbstract,
    private _router: Router,
    private _insSrv: InsRealservice,
    private _strUtils: StringUtilsService,
    private _logSrv: LogFonctionnelService,
    private _featureSrv: FeatureService
  ) {
    this._cardStatusChange = this._cpxHandleSrv.cpxCardRipped.subscribe(async (error: CardRippedStatus) => {
      if (error === CardRippedStatus.dmpConnectError) {
        this.currentDmpStep = undefined;
        this._modalSrv.openAlert(
          this._viewContainerRef,
          'Erreur',
          'Une erreur est survenue avec l\'utilitaire DmpConnect.\n\rVeuillez vérifier que celui-ci est bien en service.'
        );
      } else {
        this.currentDmpStep = undefined;
        // await this.initCpxSession();

        const loading = this._modalSrv.openLoading(this._viewContainerRef);
        try {
          const couverturePatient = await this._patientSrv.findOnePatientCouverture(this.patientId);

          if (couverturePatient && couverturePatient.insc && couverturePatient.insc !== '') {
            await this._directAuthentification({
              s_ins: couverturePatient.insc.substring(0, couverturePatient.insc.length - 1),
              s_insType: environment.dmpInsSuffix as 'D' | 'T' | 'N'
            });
          }
        } catch (e) {
          this._promptError(e.message);
        } finally {
          loading.destroy();
        }
      }

      this.cardSubtitle = undefined;
    });

    this._cardTypeSub = this._cpxHandleSrv.cpxCardTypeEmmitter.subscribe((type: CpxTypeEnum) => {
      console.log('constructor - _cardTypeSub - type : ', type);
      if (type === CpxTypeEnum.CPS) {
        this.connectionType = ConnectionType.cps;
      } else if (type === CpxTypeEnum.CPE) {
        this.connectionType = ConnectionType.cpe;
      } else if (type === CpxTypeEnum.CPF) {
        this.connectionType = ConnectionType.cpf;
      } else {
        this.connectionType = ConnectionType.unknow;
      }
    });

    const currentProfilStr = localStorage.getItem('currentProfil');
    if (currentProfilStr && currentProfilStr !== '') {
      const currentProfil = JSON.parse(currentProfilStr);
      this._dmpSrv.resourceId = currentProfil.id;
      this._insSrv.resourceId = currentProfil.id;
    }

    this._cpxCardInfosSub = this._cpxHandleSrv.cpxCardInfos.subscribe(v => {
      this._cpxCardInfosExists = !!v;
    });
  }

  private _cpxCardInfosExists: any;
  public get cpxCardInfosExists(): any {
    return this._cpxCardInfosExists;
  }

  async ngOnInit(): Promise<void> {
    this.isDmpSegurAccess = await this._featureSrv.canUseFeature(FeatureKey.DMP_SEGUR_DICA);
    this._logCallSrv = this._logSrv.getConfiguration(DomainEnum.Dmp);
    await this._initData();
  }

  async ngOnDestroy(): Promise<void> {
    if (this._cardStatusChange) {
      this._cardStatusChange.unsubscribe();
    }

    if (this._cardTypeSub) {
      this._cardTypeSub.unsubscribe();
    }

    if (this._cpxCardInfosSub) {
      this._cpxCardInfosSub.unsubscribe();
    }

    if (this.isBrisDeGlace) {
      await this._dmpSrv.reinitializeConnector(this._viewContainerRef);
    }
  }

  // public async selectPatient(patient: InsiInfosPatient): Promise<void> {
  //   const loading = this._modalSrv.openLoading(this._viewContainerRef);

  //   try {
  //     // On récupère l'identité du patient
  //     this.dmpStatus = await this._directAuthentification({
  //       s_ins: patient.mIns,
  //       s_insType: environment.dmpInsSuffix as 'D' | 'T' | 'N'
  //     });

  //     if (this.dmpStatus) {
  //       this._generateCardSubtitle(this.dmpStatus);
  //     }

  //   } catch (e) {
  //     this._promptError(e.message);
  //   } finally {
  //     loading.destroy();
  //   }
  // }

  // public findDmp(): void {
  //   this.currentDmpStep = DmpSteps.findDmp;
  //   const modalInsi = this._modalSrv.openModalInsi('Recherche dans l\'INS', this._viewContainerRef);
  //   const sub = modalInsi.instance.closeEmitter.subscribe(async (res: InsiInfosPatient) => {
  //     sub.unsubscribe();
  //     modalInsi.destroy();
  //     if (res) {
  //       await this.selectPatient(res);
  //     }
  //   });
  // }

  public async endDisplayDmp(): Promise<void> {
    this.cardSubtitle = undefined;
    this.currentDmpStep = undefined;
    // this.findDmp();
  }

  public async endAddDocuments(): Promise<void> {
    this.currentDmpStep = this.previousStep;
  }

  public async updateDocVisibility($event): Promise<void> {}

  public async archiveDoc($event): Promise<void> {}

  public addDocumentToDmp(): void {
    this.previousStep = this.currentDmpStep;
    this.currentDmpStep = DmpSteps.addDocuments;
  }

  private _logDmpAccess(): void {
    if (this._logCallSrv) {
      this._logCallSrv.addLog({
        action: ActionEnum.Read,
        result: ResultEnum.Success,
        typeTarget: TypeTargetEnum.Patient,
        idTarget: this.patientId,
        subDomain: 'Consultation DMP',
        details: 'Accès au DMP'
      });
    }
  }

  private _generateCardSubtitle(patient?: DmpHlGetDirectAuthentificationDmpStatus): void {
    if (patient && patient.ExistingTestAnswer && patient.ExistingTestAnswer.AdminData
      && this.dmpStatus.ExistingTestAnswer && this.dmpStatus.ExistingTestAnswer.i_dmpStatus === DmpStatusEnum['DMP existant']
      && (this.dmpStatus.ExistingTestAnswer.i_userAuthorization === DmpUserAuthorizationEnum.AuthorizationExist)) {
      let name;
      let birthName;

      if (patient.ExistingTestAnswer.AdminData.s_patientName !== 'NON RENSEIGNE') {
        name = patient.ExistingTestAnswer.AdminData.s_patientName;
      } else {
        name = '';
      }

      if (patient.ExistingTestAnswer?.AdminData?.s_patientBirthName) {
        birthName = patient.ExistingTestAnswer?.AdminData?.s_patientBirthName;
      }

      this.cardSubtitle = `${name}
      ${
        birthName ? '(né(e) ' + birthName + ')' : ''
      } ${
        patient.ExistingTestAnswer.AdminData.s_patientGivenName ? patient.ExistingTestAnswer.AdminData.s_patientGivenName : patient.ExistingTestAnswer.AdminData.s_patientFirstBirthGiven
      } ${patient.ExistingTestAnswer.AdminData.s_birthday && moment(patient.ExistingTestAnswer.AdminData.s_birthday).isValid() ? '(' + moment(patient.ExistingTestAnswer.AdminData.s_birthday).format('DD/MM/YYYY') + ')' : ''}`;
    }
  }

  private canAccessWithThisPatient(patient: InfosPatient): boolean {
    if (!this.patient.isIdentiteAttributOk) {
      this._promptAttributIdentiteNokAlert(UNAUTHORISZED_ATTRIBUT);
      return false;
    } else if (this.patient.identiteStatutId !== '95a6081f-5260-44b8-9661-e4795e1cfcaf') {
      this._promptAttributIdentiteNokAlert(UNAUTHORISZED_STATUS);
      return false;
    } else {
      return true;
    }
  }

  private _promptAttributIdentiteNokAlert(msg: string): void {
    this._modalSrv.openAlert(
      this._viewContainerRef,
      'Identité du patient non valide',
      msg
    );
  }

  private async _initDmpData(): Promise<void> {
    const _askConnexionSecrete = async (): Promise<void> => {
      return new Promise<void>((async (resolve, reject) => {
        try {
          const modal = this._modalSrv.openModalConfirmation(
            this._viewContainerRef,
            'Connexion secrète',
            `Le patient est mineur, voulez-vous accéder à son DMP en mode connexion secrète ?\n\nLe mode connexion secrète ne laisse pas de trace d'accès visible pour les représentants légaux et permet ainsi de protéger le secret des mineurs.`
          );
          const sub = modal.instance.closeEmitter.subscribe(async (res) => {
            if (res) {
              await this._dmpSrv.setConfidentialityLevel(this._viewContainerRef, res);
            }
            resolve();
            sub.unsubscribe();
          });
        } catch (e) {
          reject(e);
        }
      }));
    };

    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    try {
      if (!this.canAccessWithThisPatient(this.patient)) {
        return;
      }

      // On passe la confidentialité à false pour réinitialiser le confidentiality-code des appels DMP
      await this._dmpSrv.setConfidentialityLevel(this._viewContainerRef, false);

      const fichePatient = await this._patientSrv.findOnePatientFiche(this.patientId);
      const session: CpxSessionInterface = await this._cpxHandleSrv.getCpxSession(this._viewContainerRef, SessionTypeEnum.CARTE);
      if (fichePatient.autorisationAlimentationDmp) {
        const autorisation = fichePatient.autorisationAlimentationDmp.find(a => a.rpps === session.dmpConnectSessionInfos.s_internalId);
        this.autorisationAlimentation = !!autorisation?.autorisation;
      }
      const dmpParams = await this._dmpSrv.getDmpParameters(this._viewContainerRef);
      // fichePatient.dateNaissance = moment('2015-03-04', 'YYYY-MM-DD').format('YYYY-MM-DD');
      if (fichePatient && fichePatient.dateNaissance && dmpParams.minorFunctionManagement && dmpParams.majorityAge && moment(new Date()).subtract(dmpParams.majorityAge, 'year').isBefore(moment(fichePatient.dateNaissance, 'YYYY-MM-DD'))) {
        await _askConnexionSecrete();
        await this._dmpSrv.hl_getdirectauthentificationdmpstatus(this._viewContainerRef, `${fichePatient.ins}${fichePatient.insKey}${environment.dmpInsSuffix}`);
      }

      if (fichePatient && fichePatient.ins && fichePatient.insKey) {
        await this._getPatientInsIdentity({
          sex: fichePatient.sexe,
          birthDate: fichePatient.dateNaissance,
          birthName: fichePatient.nomNaissance,
          given: fichePatient.prenom,
          birthPlace: fichePatient.cpNaissance,
          name: fichePatient.nomUsage
        });

        this.dmpStatus = await this._directAuthentification({
          s_ins: `${fichePatient.ins}${fichePatient.insKey}`,
          s_insType: environment.dmpInsSuffix as 'D' | 'T' | 'N'
        });

        this.patientIns = `${fichePatient.ins}${fichePatient.insKey}`;

        if (this.dmpStatus) {
          if (this.dmpStatus.ExistingTestAnswer && this.dmpStatus.ExistingTestAnswer.i_dmpStatus === DmpStatusEnum['DMP existant']
            && (this.dmpStatus.ExistingTestAnswer.i_userAuthorization === DmpUserAuthorizationEnum.AuthorizationExist)
          ) {
            this._generateCardSubtitle(this.dmpStatus);
            this.currentDmpStep = DmpSteps.dmpAccessRight;
          } else {
            // this.currentDmpStep = DmpSteps.findDmp;
          }
        }
      } else {
        // this.currentDmpStep = DmpSteps.findDmp;
        // this.findDmp();
      }



      if (this.currentDmpStep === DmpSteps.findDmp) {
      }

      const addresses = await this._patientSrv.findOnePatientFiche(this.patientId);
      if (addresses.adresses && addresses.adresses.length > 0) {
        for (const address of addresses.adresses) {
          if (address.isPrincipale) {
            this.patientAddress = address;
          }
        }
        if (!this.patientAddress) {
          this.patientAddress = addresses.adresses[0];
        }
      }

      this.isSecretMode = session.confidentialityLevel;

    } finally {
      loading.destroy();
    }
  }

  private async _getPatientInsIdentity(patient: { sex: string, birthDate: string, birthName: string, birthPlace?: string, given: string, name?: string }): Promise<void> {
    const _getSexeIndex = (sexe: string | undefined): 1 | 2 | 3 | undefined => {
      switch (sexe) {
        case 'INDETERMINE':
        case 'I':
          return 1;
          case 'HOMME':
          case 'H':
          return 2;
        case 'FEMME':
        case 'F':
          return 3;
        default:
          return undefined;
      }
    };

    try {
      const result = await this._insSrv.getInsFromIdentityInformation(
        this._viewContainerRef, {
          i_sex: _getSexeIndex(patient.sex),
          s_birthDate: patient.birthDate,
          s_birthName: this._strUtils.removeAccents(patient.birthName).normalize('NFKD').toUpperCase(),
          s_birthPlace: patient.birthPlace,
          s_given: this._strUtils.removeAccents(patient.given).normalize('NFKD').toUpperCase()
        }
      );

      if (result.i_insIdentityResult === 2 && result.Identity) {
        this.patientInsIdentity = result.Identity;
        if (!result.Identity.s_name && patient.name) {
          this.patientInsIdentity.s_name = patient.name;
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  private async initCpxSession(): Promise<void> {
    try {
      /**
       * On interroge le service DMP pour savoir si une session est active
       * Si il y a une session on continue
       * Sinon on ouvre une modal pour demander à l'utilisateur de s'authentifier
       */
      // const useCert = await this._cpxHandleSrv.isESConnexionIndirect(SessionTypeEnum.CERT_DMP);
      // console.log('useCert : ', useCert);

      const session = await this._getDmpSession();
      if (session) {
        await this._initDmpData();
      }
      // if (!useCert && !this._cpxHandleSrv.isSessionOpen(SessionTypeEnum.CARTE)) {
      //   if (!(await this._cpxHandleSrv.showCpxModal(this._viewContainerRef, 'dmp component')).isSessionOpen) {
      //     return;
      //   }
      //   await this._initDmpData();
      // } else {
      //   await this._getDmpSession();
      //   await this._initDmpData();
      // }
    } catch (e) {
      this._promptError(e.message);
    }
  }

  public async displayMyDocuments(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);
    try {
      const cpxSession = await this._cpxHandleSrv.getCpxSession(this._viewContainerRef, SessionTypeEnum.CARTE);
      if (cpxSession && (cpxSession.cpxType === CpxTypeEnum.CPS || cpxSession.cpxType === CpxTypeEnum.CPF || cpxSession.cpxType === CpxTypeEnum.CPE)) {
        const docs = await this._dmpSrv.getMyDocuments({
          patientIns: this.patientIns,
          rpps: cpxSession.dmpConnectSessionInfos.s_internalId
        });
        const docsTable: Array<MyDocumentTableDisplay> = [];
        for (const doc of docs) {
          docsTable.push({
            title: doc.title,
            meta: doc,
          });
        }

        this.myDocsTable = {
          objects: docsTable,
          page: 0,
          perpage: docsTable.length,
          total: docsTable.length
        };
      }
      this.currentDmpStep = DmpSteps.myDocuments;
    } catch (e) {
      this._promptError(e.message);
    } finally {
      loading.destroy();
    }
  }

  public async openWebPsSpace(): Promise<void>{
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      // On récupère les informations de l'espace Web PS
      const urls = await this._dmpSrv.hl_getwebpsdmpurls(this._viewContainerRef, { s_ins: `${this.patient.ins}${this.patient.insKey}`, s_insType: environment.dmpInsSuffix as 'T' | 'D' | 'N' });

      if (urls) {
        // On navigue vers l'espace Web PS
        // On navigue vers l'espace Web PS
        if (isAllowedExternalUrl(urls.Urls[1])) {
          window.open(urls.Urls[1]);
        } else if (isAllowedExternalUrl(urls.Urls[0])) {
          window.open(urls.Urls[0]);
        }
      } else {
        this._modalSrv.openAlert(
          this._viewContainerRef,
          'Pas de lien',
          'Aucune URL n\'est disponible'
        );
      }
    } catch (e) {
      this._promptError(e.message);
    } finally {
      loading.destroy();
    }
  }

  public async deleteDocFromUser(doc: MyDocumentTableDisplay): Promise<void> {
    const cpxSession = await this._cpxHandleSrv.getCpxSession(this._viewContainerRef, SessionTypeEnum.CARTE);
    if (!cpxSession) {
      return;
    }
    const modalConfirm = this._modalSrv.openModalConfirmation(
      this._viewContainerRef,
      'Confirmation de suppression',
      `Voulez-vous supprimer le document ${doc.meta.title} ?`
    );
    const confirmSub = modalConfirm.instance.closeEmitter.subscribe(async v => {
      if (v) {
        await this._dmpSrv.hl_deletedocument(
          this._viewContainerRef,
          {
            s_healthcareSettings: cpxSession.PracticeLocations[cpxSession.practiceSettingIndice].s_practiceLocationActivity,
            s_ins: {
              s_ins: `${this.patient.ins}${this.patient.insKey}`,
              s_insType: environment.dmpInsSuffix as 'T' | 'D' | 'N'
            },
            s_uniqueId: doc.meta.uniqueId,
            s_title: doc.title
          },
          this.patient.id
        );
        await this._dmpSrv.deleteFromMyDocuments(doc.meta.uniqueId);
        await this.displayMyDocuments();
      }
      confirmSub.unsubscribe();
      modalConfirm.destroy();
    });
  }

  private async _initData(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      this.patientId = await this._getPatientId();
      this.patient = await this._patientSrv.findOnePatientInfos(this.patientId);

      if (!this.canAccessWithThisPatient(this.patient)) {
        return;
      }

      await this.initCpxSession();

      // if (this.currentDmpStep === DmpSteps.findDmp) {
      //   const couverturePatient = await this._patientSrv.findOnePatientCouverture(this.patientId);

      //   if (couverturePatient && couverturePatient.insc && couverturePatient.insc !== '') {
      //     this.dmpStatus = await this._directAuthentification({
      //       s_ins: couverturePatient.insc.substring(0, couverturePatient.insc.length - 1),
      //       s_insType: environment.dmpInsSuffix as 'D' | 'T' | 'N'
      //     });

      //     if (this.dmpStatus) {
      //       this._generateCardSubtitle(this.dmpStatus);
      //     }
      //   }
      // }

      // const addresses = await this._patientSrv.findOnePatientFiche(this.patientId);
      // if (addresses.adresses && addresses.adresses.length > 0) {
      //   for (const address of addresses.adresses) {
      //     if (address.isPrincipale) {
      //       this.patientAddress = address;
      //     }
      //   }

      //   if (!this.patientAddress) {
      //     this.patientAddress = addresses.adresses[0];
      //   }
      // }
    } catch (e) {
      if (e.message === 'Le cadre d\'exercice n\'est pas renseigné dans les paramètres') {
        const modal = this._modalSrv.openModalMultipleChoice(
          this._viewContainerRef,
          'Informations manquantes',
          `${e.message}`,
          '',
          [{
            label: 'Accéder aux paramètre de DMP',
            value: 'goToParams'
          }, {
            label: 'Fermer',
            value: 'close',
            backgroundColor: 'var(--darkest-primary)'
          }]
        );

        const sub = modal.instance.closeEmitter.subscribe((val: string) => {
          if (val === 'goToParams') {
            this._router.navigateByUrl('/user/parametres/dmp');
          }
          sub.unsubscribe();
          modal.destroy();
        });
      } else {
        this._promptError(e.message);
      }
    } finally {
      loading.destroy();
    }
  }

  private async _setAutorisationAlimentationDmp(autorisation: boolean): Promise<void> {
  const fichePatient = await this._patientSrv.findOnePatientFiche(this.patientId);
  const session = await this._cpxHandleSrv.getCpxSession(this._viewContainerRef, SessionTypeEnum.CARTE);
  
  try {
    await this._patientSrv.putAutorisationAlimentationDmp({
      patientId: fichePatient.id,
      rpps: session.dmpConnectSessionInfos.s_internalId,
      autorisation
    });
    await this._createLogAlimentationDmp(autorisation, true);
    this.autorisationAlimentation = autorisation;
  } catch (error) {
    await this._createLogAlimentationDmp(autorisation, false);
    throw error;
  }
}

  private async _createLogAlimentationDmp(autorisation: boolean, isSuccess: boolean) {
  const detailMessage = autorisation 
    ? `Le patient (ou son représentant légal), préalablement informé, n’a pas exprimé d’opposition pour l’alimentation de son DMP`
    : `Le patient (ou son représentant légal), préalablement informé, a exprimé son opposition à l’alimentation de son DMP`;

  const resultat = isSuccess ? ResultEnum.Success : ResultEnum.Error;
  try {
    await this._logCallSrv.addLog({
      action: ActionEnum.Update,
      result: resultat,
      typeTarget: TypeTargetEnum.Patient,
      idTarget: this.patientId,
      subDomain: "Autorisation d'alimentation",
      details: detailMessage,
    });
  } catch (error) {
    console.error("Impossible de sauvegarder le log d'alimentation DMP", error);
  }
}


  private async _getAutorisationAlimentationDmp(): Promise<boolean> {
    const fichePatient = await this._patientSrv.findOnePatientFiche(this.patientId);
    const session = await this._cpxHandleSrv.getCpxSession(this._viewContainerRef, SessionTypeEnum.CARTE);
    if (fichePatient.autorisationAlimentationDmp) {
      const autorisation = fichePatient.autorisationAlimentationDmp.find(a => a.rpps === session.dmpConnectSessionInfos.s_internalId);
      if (autorisation) {
        return autorisation.autorisation;
      }
    }
    return false;
  }

  public async handleAccessRight(patientIns?: DmpHlFindPatientResponseSIns, isManual = false): Promise<DmpHlGetDirectAuthentificationDmpStatus | undefined> {
    if (!patientIns && this.patient && this.patient.ins && this.patient.insKey) {
      patientIns = {
        s_ins: `${this.patient.ins}${this.patient.insKey}`,
        s_insType: environment.dmpInsSuffix as 'D' | 'T' | 'N'
      };
    }

    return new Promise<DmpHlGetDirectAuthentificationDmpStatus | undefined>(async (resolve, reject) => {
      try {
        if (!patientIns) {
          resolve(undefined);
        }

        // ---- DEBUT AUTOMATISATION SC.DMP/CONF.16 ----
        // Conformité Ségur SC.DMP/CONF.16 :
        // Cette automatisation n'est exécutée QUE si la feature DMP_SEGUR_DICA est activée (isDmpSegurAccess).
        // Si la feature n'est pas activée, le système conserve son comportement historique et ouvre
        // systématiquement la modale de demande d'accès classique (DmpAccessRequestModalComponent).
        //
        // Lorsqu'un professionnel de santé non encore autorisé (Utilisateur 2) accède au DMP d'un patient
        // dont le consentement à la consultation a déjà été recueilli au sein de la structure, le système
        // doit positionner automatiquement son autorisation individuelle (TD0.3) sans action supplémentaire.
        // TODO [PSC] : Dès que l'authentification Pro Santé Connect (PSC) sera intégrée :
        // 1. Adapter la condition d'authentification pour inclure ConnectionType.psc (ou session PSC valide)
        // 2. Adapter la récupération de la session (ex: SessionTypeEnum.PSC ou session SSO/OpenID) pour extraire le RPPS du praticien
        if (this.isDmpSegurAccess && !isManual && this.patient.dmpConsentementConsultation === true && (this.connectionType === ConnectionType.cps || this.connectionType === ConnectionType.cpf)) {
           try {
             // 1. Identification de l'utilisateur connecté (carte CPS / RPPS)
             // TODO [PSC] : Permettre la récupération des infos de session PSC en plus de SessionTypeEnum.CARTE
             const session = await this._cpxHandleSrv.getCpxSession(this._viewContainerRef, SessionTypeEnum.CARTE);
             const rpps = session?.dmpConnectSessionInfos?.s_internalId || 'RPPS non renseigné';
             const rppsNom = session?.dmpConnectSessionInfos?.s_name || 'Utilisateur connecté';

             // 2. Trace du retour TD0.2 : contrôle initial montrant l'absence d'autorisation individuelle sur le serveur DMP
             if (this._logCallSrv) {
               await this._logCallSrv.addLog({
                 action: ActionEnum.Read,
                 result: ResultEnum.Success,
                 typeTarget: TypeTargetEnum.Patient,
                 idTarget: this.patientId,
                 subDomain: "Vérification accès DMP (TD0.2)",
                 details: `Contrôle TD0.2 pour ${rppsNom} (${rpps}) : DMP existant, autorisation individuelle non présente sur le serveur national.`
               });

               // 3. Trace de la règle métier : le consentement localement recueilli prend le dessus
               await this._logCallSrv.addLog({
                 action: ActionEnum.Update,
                 result: ResultEnum.Success,
                 typeTarget: TypeTargetEnum.Patient,
                 idTarget: this.patientId,
                 subDomain: "Consentement consultation DMP",
                 details: `Consentement consultation préalablement enregistré au cabinet : application automatique des droits pour ${rppsNom} sans nouvelle sollicitation du patient.`
               });
             }

             await this._dmpSrv.hl_setdmpaccessmode(this._viewContainerRef, {
               i_accessMode: DmpHlAccessModeEnum['Accès normal'],
               s_reason: undefined
             });
             
             // 4. Transaction TD0.3 : Positionnement de l'autorisation d'accès individuelle
             await this._dmpSrv.hl_updateuserdmpaccessauthorization(this._viewContainerRef, {
               i_action: DmpAuthorizationActionEnum['ajouter autorisation'],
               i_setTreatingPhysician: false,
               s_ins: patientIns
             });

             // 5. Trace du succès du positionnement automatique (TD0.3)
             if (this._logCallSrv) {
               await this._logCallSrv.addLog({
                 action: ActionEnum.Update,
                 result: ResultEnum.Success,
                 typeTarget: TypeTargetEnum.Patient,
                 idTarget: this.patientId,
                 subDomain: "Positionnement autorisation DMP (TD0.3)",
                 details: `Autorisation individuelle de consultation positionnée avec succès (TD0.3) pour ${rppsNom} (${rpps}).`
               });
             }

             await this._createLogConsultationDmp('explicite', true);

             this.dmpAccessRight = DmpAccessRight.authorized;
             this.isBrisDeGlace = false;
             this._logDmpAccess();

             this.dmpStatus = await this._dmpSrv.hl_getdirectauthentificationdmpstatus(this._viewContainerRef, `${patientIns.s_ins}${environment.dmpInsSuffix}`);
             this.displayDmp = this.dmpStatus;
             
             resolve(this.dmpStatus);
             return;
           } catch (error) {
             console.error("Impossible d'enregistrer l'autorisation DMP automatique", error);
             await this._createLogConsultationDmp('explicite', false);
             // En cas d'échec de l'automatisation, on laisse la suite du code ouvrir la modale
           }
        }

        if (this.isDmpSegurAccess && !isManual && this.patient.dmpConsentementConsultation === false) {
             // Trace légale Ségur : tentative de consultation bloquée par opposition patient
             if (this._logCallSrv) {
               await this._logCallSrv.addLog({
                 action: ActionEnum.Read,
                 result: ResultEnum.Error,
                 typeTarget: TypeTargetEnum.Patient,
                 idTarget: this.patientId,
                 subDomain: "Consultation du DMP",
                 details: "Tentative de consultation bloquée : le patient s'oppose expressément à la consultation de son DMP."
               });
             }
             this.dmpAccessRight = DmpAccessRight.unauthorized;
             this.currentDmpStep = DmpSteps.myDocuments;
             this.displayDmp = this.dmpStatus;
             resolve(this.dmpStatus);
             return;
        }
        // ---- FIN AUTOMATISATION SC.DMP/CONF.16 ----

        const autorisation = await this._getAutorisationAlimentationDmp();
        let autorisationAccessDmp: 'explicite' | 'bris-de-glace' | 'refus' | undefined = undefined;
        if (this._canAccessPatientDmp(this.dmpStatus)) {
          if (this._dmpSrv.accessMode === DmpHlAccessModeEnum['Accès normal']) {
            autorisationAccessDmp = 'explicite';
          } else if (this._dmpSrv.accessMode === DmpHlAccessModeEnum['Accès "Bris de glace"']) {
            autorisationAccessDmp = 'bris-de-glace';
          }
        }
        const modal = this._modalSrv.openModal<DmpAccessRequestModalComponent>(
          this._viewContainerRef,
          DmpAccessRequestModalComponent
        ) as ComponentRef<DmpAccessRequestModalComponent>;
        if (this.dmpStatus.ExistingTestAnswer.AdminData.s_patientName && this.dmpStatus.ExistingTestAnswer.AdminData.s_patientName !== '' && this.dmpStatus.ExistingTestAnswer.AdminData.s_patientName !== 'NON RENSEIGNE') {
          modal.instance.patientName = `${this.dmpStatus.ExistingTestAnswer.AdminData.s_patientName} ${this.dmpStatus?.ExistingTestAnswer?.AdminData?.s_patientBirthName ? '(né(e) ' + this.dmpStatus.ExistingTestAnswer.AdminData.s_patientBirthName + ')' : '' } ${this.dmpStatus.ExistingTestAnswer.AdminData.s_patientFirstBirthGiven ? this.dmpStatus.ExistingTestAnswer.AdminData.s_patientFirstBirthGiven : this.dmpStatus.ExistingTestAnswer.AdminData.s_patientGivenName}`;
        } else {
          modal.instance.patientName = `${this.dmpStatus.ExistingTestAnswer.AdminData.s_patientBirthName} ${this.dmpStatus.ExistingTestAnswer.AdminData.s_patientFirstBirthGiven ? this.dmpStatus.ExistingTestAnswer.AdminData.s_patientFirstBirthGiven : this.dmpStatus.ExistingTestAnswer.AdminData.s_patientGivenName}`;
        }
        modal.instance.connectionType = this.connectionType;
        modal.instance.autorisationAlimentationDmp = autorisation;
        modal.instance.autorisationAccessDmp = autorisationAccessDmp;
        const sub = modal.instance.closeEmitter.subscribe(async (confirm?: DmpAutorizationForm) => {

          // On enregistre en DB l'autorisation donnée par le patient, seulement si la valeur a changé
          if (confirm && !!confirm.alimentation !== autorisation) {
            await this._setAutorisationAlimentationDmp(!!confirm.alimentation);
          }

          // ---- SC.DMP/CONF.16: Sauvegarde du consentement recueilli dans la modale ----
          // N'est synchronisé dans les colonnes Ségur que si la feature DMP_SEGUR_DICA est active
          if (this.isDmpSegurAccess && confirm && confirm.autorization === 'explicite' && this.patient.dmpConsentementConsultation !== true) {
             try {
                this.patient.dmpConsentementConsultation = true;
                await this._patientSrv.updatePatientInfos(this.patient.id, this.patient);
             } catch(e) {
                console.error("Impossible de sauvegarder le consentement HMO", e);
             }
          } else if (this.isDmpSegurAccess && confirm && confirm.autorization === 'refus' && this.patient.dmpConsentementConsultation !== false) {
             try {
                this.patient.dmpConsentementConsultation = false;
                await this._patientSrv.updatePatientInfos(this.patient.id, this.patient);
             } catch(e) {
                console.error("Impossible de sauvegarder le refus HMO", e);
             }
          }
          // ----

          if (this.connectionType !== ConnectionType.cpe) {
            if (confirm && confirm.autorization && !confirm.brisDeGlaceReason) {
              this._dmpSrv.accessMode = DmpHlAccessModeEnum['Accès normal'];
            } else if (confirm && confirm.autorization && confirm.brisDeGlaceReason) {
              this._dmpSrv.accessMode = DmpHlAccessModeEnum['Accès "Bris de glace"'];
            } else if (confirm && !confirm.autorization) {
              this._dmpSrv.accessMode = undefined;
              if (this.dmpStatus && this.dmpStatus.ExistingTestAnswer.i_userAuthorization === DmpUserAuthorizationEnum.AuthorizationExist) {
                try {
                await this._dmpSrv.hl_updateuserdmpaccessauthorization(this._viewContainerRef, {
                  i_action: DmpAuthorizationActionEnum['retirer autorisation'],
                  i_setTreatingPhysician: this.dmpStatus.i_treatingPhysician,
                  s_ins: patientIns
                });
                this.dmpStatus.ExistingTestAnswer.i_userAuthorization = DmpUserAuthorizationEnum.NoAuthorization;
                if (autorisationAccessDmp !== 'refus') {
                  await this._createLogConsultationDmp('refus', true);
                }
              } catch (error) {
                console.error("Impossible de retirer l'autorisation DMP", error);
                if (autorisationAccessDmp !== 'refus') {
                  await this._createLogConsultationDmp('refus', false);
                }
              }
            }
          }
          }
 
          if (confirm && confirm.autorization) {
            if (this.connectionType === ConnectionType.cps || this.connectionType === ConnectionType.cpf) {
              // On enregistre la confirmation
              try {
                await this._dmpSrv.hl_setdmpaccessmode(this._viewContainerRef, {
                  i_accessMode: !confirm.brisDeGlaceReason ? DmpHlAccessModeEnum['Accès normal'] : DmpHlAccessModeEnum['Accès "Bris de glace"'],
                  s_reason: confirm.brisDeGlaceReason && confirm.brisDeGlaceReason !== '' ? confirm.brisDeGlaceReason : undefined
                });

                if (!confirm.brisDeGlaceReason) {
                  await this._dmpSrv.hl_updateuserdmpaccessauthorization(this._viewContainerRef, {
                    i_action: DmpAuthorizationActionEnum['ajouter autorisation'],
                    i_setTreatingPhysician: false,
                    s_ins: patientIns
                  });
                  if (autorisationAccessDmp !== 'explicite') {
                    await this._createLogConsultationDmp('explicite', true);
                  }
                } else {
                  if (autorisationAccessDmp !== 'bris-de-glace') {
                    await this._createLogConsultationDmp('bris-de-glace', true, confirm.brisDeGlaceReason);
                  }
                }
              } catch (error) {
                console.error("Impossible d'enregistrer l'autorisation DMP", error);
                const type = confirm.brisDeGlaceReason ? 'bris-de-glace' : 'explicite';
                if (autorisationAccessDmp !== type) {
                  await this._createLogConsultationDmp(type, false, confirm.brisDeGlaceReason);
                }
              }
            } else if (this.connectionType === ConnectionType.cpe) {
              // await this._dmpSrv.hl_updateuserdmpaccessauthorization(this._viewContainerRef, {
              //   i_action: DmpAuthorizationActionEnum['ajouter autorisation'],
              //   i_setTreatingPhysician: false,
              //   s_ins: patientIns
              // });
            }

            this.dmpAccessRight = DmpAccessRight.authorized;
            this.isBrisDeGlace = confirm.brisDeGlaceReason && confirm.brisDeGlaceReason !== '' ? true : false;
            this._logDmpAccess();

            this.dmpStatus = await this._dmpSrv.hl_getdirectauthentificationdmpstatus(this._viewContainerRef, `${patientIns.s_ins}${environment.dmpInsSuffix}`);
          } else if (confirm && !confirm.autorization) {
            this.dmpAccessRight = DmpAccessRight.unauthorized;
            this.currentDmpStep = DmpSteps.myDocuments;
          } else {
            this.currentDmpStep = DmpSteps.dmpAccessRight;
            resolve(undefined);
            modal.destroy();
            sub.unsubscribe();
            return;
          }

          this.displayDmp = this.dmpStatus;
          modal.destroy();
          sub.unsubscribe();
          resolve(this.dmpStatus);
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  private async _createLogConsultationDmp(
  type: AccesDmpType, 
  isSuccess: boolean, 
  motif?: string
) {
  const messages: Record<AccesDmpType, string> = {
    'explicite':      "Le patient (ou son représentant légal), préalablement informé, consent à la consultation de son DMP",
    'bris-de-glace':  `Accès en urgence au DMP (Bris de glace), motif : ${motif}`,
    'refus':          "Le patient (ou son représentant légal), préalablement informé, n'a pas donné / a retiré son autorisation de consultation de son DMP"
  };
  await this._logCallSrv.addLog({
    action: ActionEnum.Update,
    result: isSuccess ? ResultEnum.Success : ResultEnum.Error,
    typeTarget: TypeTargetEnum.Patient,
    idTarget: this.patientId,
    subDomain: "Consultation du DMP",
    details: messages[type]
  });
}

  private _canAccessPatientDmp(dmpStatus: DmpHlGetDirectAuthentificationDmpStatus): boolean {
    return dmpStatus?.ExistingTestAnswer?.i_dmpStatus === DmpStatusEnum['DMP existant']
      && dmpStatus?.ExistingTestAnswer?.i_userAuthorization === DmpUserAuthorizationEnum.AuthorizationExist;
  }

  private async _directAuthentification(patientIns: DmpHlFindPatientResponseSIns): Promise<DmpHlGetDirectAuthentificationDmpStatus | undefined> {
    return new Promise<DmpHlGetDirectAuthentificationDmpStatus>(async (resolve, reject) => {
      try {
        // On vérifie l'existance et les droits d'accès au DMP et le cas échéant on demande les droits
        this.dmpStatus = await this._dmpSrv.hl_getdirectauthentificationdmpstatus(this._viewContainerRef, `${patientIns.s_ins}${environment.dmpInsSuffix}`);

        if (!this.dmpStatus) {
          resolve(undefined);
        }

        if (this.dmpStatus.i_treatingPhysician) {
          this.amIMt = true;
        }

        if (
          this._canAccessPatientDmp(this.dmpStatus)
          && (this.connectionType === ConnectionType.cps || this.connectionType === ConnectionType.cpf)
        ) {
          resolve(this.dmpStatus);
          this.displayDmp = this.dmpStatus;
          this.currentDmpStep = DmpSteps.dmpAccessRight;
          this.dmpAccessRight = DmpAccessRight.authorized;
          this._dmpSrv.accessMode = DmpHlAccessModeEnum['Accès normal'];
          this._logDmpAccess();
        } else if (this.dmpStatus.ExistingTestAnswer.i_dmpStatus === DmpStatusEnum['DMP existant']
          && ((this.dmpStatus.ExistingTestAnswer.i_userAuthorization === DmpUserAuthorizationEnum.AuthorizationExpired)
            || (this.dmpStatus.ExistingTestAnswer.i_userAuthorization === DmpUserAuthorizationEnum.NoAuthorization)
            || this.connectionType === ConnectionType.cpe)
        ) {
          resolve(await this.handleAccessRight(patientIns));
        } else if (this.dmpStatus.ExistingTestAnswer.i_dmpStatus === DmpStatusEnum['DMP existant']
          && this.dmpStatus.ExistingTestAnswer.i_userAuthorization === DmpUserAuthorizationEnum.AuthorizationDenied
        ) {
          // Trace légale Ségur : accès refusé au niveau national par le patient (TD0.2)
          if (this._logCallSrv) {
            await this._logCallSrv.addLog({
              action: ActionEnum.Read,
              result: ResultEnum.Error,
              typeTarget: TypeTargetEnum.Patient,
              idTarget: this.patientId,
              subDomain: "Consultation du DMP",
              details: "Accès au DMP refusé : le praticien ne dispose pas de l'autorisation d'accès requise au niveau national (AuthorizationDenied)."
            });
          }
          resolve(this.dmpStatus);
          this._modalSrv.openAlert(
            this._viewContainerRef,
            'DMP interdit',
            'L\'accès à ce DMP vous est interdit.'
          );
        } else if (this.dmpStatus.ExistingTestAnswer.i_dmpStatus === DmpStatusEnum['DMP fermé']) {
          resolve(this.dmpStatus);
          this._modalSrv.openAlert(
            this._viewContainerRef,
            'DMP fermé',
            `Ce DMP est fermé.\nDate de fermeture : ${moment(this.dmpStatus.ExistingTestAnswer.s_closingDate).format('DD/MM/YYYY')}\nMotif de fermeture : ${this.dmpStatus.ExistingTestAnswer.s_closingMessage}`
          );
        } else if (this.dmpStatus.ExistingTestAnswer.i_dmpStatus === DmpStatusEnum['DMP inexistant']) {
          resolve(this.dmpStatus);
          this._modalSrv.openAlert(
            this._viewContainerRef,
            'DMP inexistant',
            'Ce DMP n\'existe pas'
          );
        } else if (this.dmpStatus.ExistingTestAnswer.i_dmpStatus === DmpStatusEnum.Erreur) {
          resolve(this.dmpStatus);
          this._modalSrv.openAlert(
            this._viewContainerRef,
            'Erreur',
            'Ce DMP contient une erreur'
          );
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  private async _getDmpSession(): Promise<CpxSessionInterface | undefined> {
    const useCert = await this._cpxHandleSrv.isESConnexionIndirect(SessionTypeEnum.CERT_DMP);
    const typeSession = useCert ? SessionTypeEnum.CERT_DMP : SessionTypeEnum.CARTE;
    let rtd: CpxSessionInterface | undefined = undefined;
    if (this._cpxHandleSrv.isSessionOpen(typeSession)) {
      rtd = await this._cpxHandleSrv.getCpxSession(this._viewContainerRef, typeSession);
    }

    if (rtd && rtd.practiceSettingIndice !== undefined && rtd.practiceSettingIndice !== null) {
      this._setCpxType(rtd);
      return rtd;
    } else if (rtd && (rtd.practiceSettingIndice === undefined || rtd.practiceSettingIndice === null)) {
      await this._cpxHandleSrv.closeSession(SessionTypeEnum.CARTE);
      return await this._getDmpSession();
    } else {
      return undefined;
    }
  }

  private _setCpxType(session: CpxSessionInterface | undefined): void {
    if (session && session.cpxType === CpxTypeEnum.CPS) {
      this.connectionType = ConnectionType.cps;
    } else if (session && session.cpxType === CpxTypeEnum.CPE) {
      this.connectionType = ConnectionType.cpe;
    } else if (session && session.cpxType === CpxTypeEnum.CPF) {
      this.connectionType = ConnectionType.cpf;
    } else {
      this.connectionType = ConnectionType.unknow;
    }
  }

  private async _getPatientId(): Promise<string> {
    return new Promise<string>(async (resolve) => {
      if (this.patientId) {
        resolve(this.patientId);
      } else {
        if (this._activatedRoute.parent && this._activatedRoute.parent.parent) {
          const patientId = this._activatedRoute.parent.parent.snapshot.paramMap.get('id');
          if (!patientId) {
            setTimeout(async () => {
              resolve(await this._getPatientId());
            }, 200);
          } else {
            resolve(patientId);
          }
        }
      }
    });
  }

  private _promptError(msg: string): void {
    let contextMsg = 'Une erreur est survenue';

    const msgSplit = msg.split('\n');
    if (msgSplit.length > 1) {
      const obj = {};
      for (const str of msgSplit) {
        const strSplit = str.split(/ : (.*)/s);
        obj[strSplit[0]] = Number.isNaN(Number(strSplit[1])) ? strSplit[1] : Number(strSplit[1]);
      }
      if (typeof obj === 'object') {
        if ((obj as any).s_apiErrorTypeDescription === 'DMPError') {
          contextMsg = `${(obj as any).s_apiErrorDescription}`;
          contextMsg += `\n${(obj as any).s_apiErrorDmpErrorDetails}`;

          msg = `Code d'erreur : ${(obj as any).s_apiErrorDmpErrorCode}`;
          msg += `\nType de l'erreur : ${(obj as any).s_apiErrorTypeDescription}`;
          msg += `\nContexte de l'erreur : ${(obj as any).s_apiErrorContext}`;
          msg += `\nDétails de l'erreur : ${(obj as any).s_apiErrorExtendedInformations}`;
        } else if ((obj as any).s_apiErrorTypeDescription === 'ApiError') {
          contextMsg = `Code d'erreur : ${(obj as any).i_apiErrorCode}`;
          contextMsg += `\nType de l'erreur : ${(obj as any).s_apiErrorTypeDescription}`;
          contextMsg += `\nContexte de l'erreur : ${(obj as any).s_apiErrorContext}`;
          contextMsg += `\nDescription de l'erreur : ${(obj as any).s_apiErrorDescription}`;
          contextMsg += `\nDétails de l'erreur : ${(obj as any).s_apiErrorExtendedInformations}`;
          msg = undefined;
        } else if ((obj as any).s_status && ((obj as any).s_status === 'ERROR' || (obj as any).s_status === 'API_ERROR')) {
          contextMsg = (obj as any).s_apiErrorDescription ? (obj as any).s_apiErrorDescription : contextMsg;
          if ((obj as any).s_apiErrorDmpErrorCode && (obj as any).s_apiErrorTypeDescription && (obj as any).s_apiErrorDmpErrorDetails) {
            msg = `Code d'erreur : ${(obj as any).s_apiErrorDmpErrorCode}`;
            msg += `\nType de l'erreur : ${(obj as any).s_apiErrorTypeDescription}`;
            msg += `\nContexte de l'erreur : ${(obj as any).s_apiErrorContext}`;
            msg += `\nDétails de l'erreur : ${(obj as any).s_apiErrorDmpErrorDetails}`;
          } else {
            msg = (obj as any).s_apiErrorDmpErrorDetails ? (obj as any).s_apiErrorDmpErrorDetails : msg;
          }
        }

        if ((obj as any).s_requestFrameInBase64) {
          console.error('Message d\'erreur pour le support (request) : ', (obj as any).s_requestFrameInBase64);
        }
        if ((obj as any).s_answerFrameInBase64) {
          console.error('Message d\'erreur pour le support (response) : ', (obj as any).s_answerFrameInBase64);
        }
      }
    }

    this._modalSrv.openAlert(
      this._viewContainerRef,
      'Erreur',
      contextMsg,
      undefined,
      msg
    );
  }
}

